import { TEST_ID_SIGNIN_ERROR, TEST_ID_SIGNIN_ERROR_TEXT } from "e2e/testId";
import { AlertCircleIcon } from "lucide-react-native";
import { RefObject, memo } from "react";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
  UseFormGetValues,
} from "react-hook-form";
import {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  TextInputProps,
  TextInputSelectionChangeEventData,
} from "react-native";

import DialCodePicker from "@/features/auth/components/organisms/DialCodePicker";
import MobileNumberInput from "@/features/auth/components/molecules/MobileNumberInput";
import Screen from "@/features/auth/components/organisms/Screen";
import { signInCountryCodesData } from "@/features/auth/signInCountryPolicy";

// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 compound-button waiver: Button/ButtonText with ButtonText children + data-driven className.
import { Button, ButtonText } from "@/components/ui/button";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from "@/components/atoms/FormControl";
import { Stack } from "@/components/atoms/Stack";

import { ifWeb } from "@/utils/platform";

/**
 * Sign-in form data structure
 * @property mobileNumber - User phone number formatted for selected country
 * @property countryCode - International dialing code prefix (e.g., "+1", "+44")
 */
export interface IForm {
  mobileNumber: string;
  countryCode: string;
}

/**
 * Props for SignInView component
 * @property root0.inputMobileNumberRef - Ref to mobile number input field for cursor control
 * @property root0.control - React Hook Form control object for form state management
 * @property root0.errors - Form validation errors for display
 * @property root0.valid - Whether form is valid and ready for submission
 * @property root0.maxLengthInput - Maximum input length based on selected country code format
 * @property root0.widthContent - Content width for responsive layout
 * @property root0.getValues - Function to retrieve current form values
 * @property root0.onSubmit - Callback invoked when sign-in form is submitted
 * @property root0.loading - Whether sign-in request is in progress
 * @property root0.isDark - Whether dark theme is active
 */
interface Props {
  control: Control<IForm>;
  valid: boolean;
  widthContent: number;
  onSubmit: () => void;
  loading: boolean;
  isDark: boolean;
  /** The memoized mobile-number field renderer, built by the Container. */
  renderMobileNumber: ReturnType<typeof makeMobileNumberField>;
}

/** What the mobile-number field needs. */
export interface MobileNumberFieldOptions {
  /** Ref to the number input, for cursor control. */
  readonly inputMobileNumberRef: RefObject<TextInputProps>;
  /** Form validation errors. */
  readonly errors: FieldErrors<IForm>;
  /** Reads the sibling country code. */
  readonly getValues: UseFormGetValues<IForm>;
  /** Maximum digits for the selected country's format. */
  readonly maxLengthInput: number;
  /** Whether the dark theme is active. */
  readonly isDark: boolean;
  /** Whether the sign-in request is in progress. */
  readonly loading: boolean;
  /** Submit handler. */
  readonly onSubmit: () => void;
  /** Selection-change handler for the number input. */
  readonly onSelectionMobileNumber: (
    e: NativeSyntheticEvent<TextInputSelectionChangeEventData>
  ) => void;
  /** Key-press handler for the number input. */
  readonly onKeyPressMobileNumber: (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>
  ) => void;
}

/**
 * Builds the `Controller` render prop for the mobile-number field.
 *
 * A factory so the Container can memoize it: `Controller` re-renders the field
 * whenever `render` changes identity, which is why this was a `useCallback` —
 * one that sat in JSX attribute position inside the View, and so was a hook in
 * a View (SE-8019).
 *
 * Its sibling `renderCountryCodeField` needs no factory because it closes over
 * nothing.
 * @param options - What the field needs from the screen.
 * @param options.getValues - Reads the sibling country code.
 * @param options.errors - Form validation errors.
 * @param options.inputMobileNumberRef - Ref for the number input.
 * @param options.isDark - Whether the dark theme is resolved.
 * @param options.loading - Whether submission is in progress.
 * @param options.maxLengthInput - Max digits for the resolved country.
 * @param options.onKeyPressMobileNumber - Key handler.
 * @param options.onSelectionMobileNumber - Selection handler.
 * @param options.onSubmit - Submit handler.
 * @returns The render prop.
 */
export const makeMobileNumberField =
  ({
    getValues,
    errors,
    inputMobileNumberRef,
    isDark,
    loading,
    maxLengthInput,
    onKeyPressMobileNumber,
    onSelectionMobileNumber,
    onSubmit,
  }: MobileNumberFieldOptions) =>
  ({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IForm, "mobileNumber">;
  }) => (
    <FormControl
      size="lg"
      isDisabled={false}
      isInvalid={!loading && !!errors.mobileNumber}
      isReadOnly={false}
      isRequired={false}
      className="flex-1"
    >
      <MobileNumberInput
        inputRef={inputMobileNumberRef}
        value={value}
        countryCode={getValues("countryCode")}
        onChange={onChange}
        onBlur={onBlur}
        onSubmit={onSubmit}
        onSelectionChange={onSelectionMobileNumber}
        onKeyPress={onKeyPressMobileNumber}
        maxLength={maxLengthInput}
        isDark={isDark}
      />
      {/* SE-6889: the DURABLE failure signal, and the counterpart to
          CONFIRM_CODE.ERROR one screen later. FormControlError renders `null`
          unless the enclosing FormControl is invalid AND it has children, so
          the presence of this region is itself the assertion — no timer takes
          it away and no copy has to be matched. It already carried yup's
          validation message; the container now also writes the sign-in OUTCOME
          here, so the region appears for every rejection branch, including
          messages the client has never seen. */}
      <FormControlError testID={TEST_ID_SIGNIN_ERROR}>
        <FormControlErrorIcon as={AlertCircleIcon} />
        <FormControlErrorText
          testID={TEST_ID_SIGNIN_ERROR_TEXT}
          accessibilityRole="alert"
          accessibilityLiveRegion="assertive"
          className="shrink pl-1"
        >
          {errors.mobileNumber?.message}
        </FormControlErrorText>
      </FormControlError>
    </FormControl>
  );

const renderCountryCodeField = ({
  field: { onChange, value },
}: {
  field: ControllerRenderProps<IForm, "countryCode">;
}) => (
  <FormControl
    isDisabled={false}
    isReadOnly={false}
    isRequired={false}
    size="lg"
  >
    <Stack className="w-28">
      <DialCodePicker
        data={signInCountryCodesData}
        defaultValue={value}
        placeholder="Code"
        onChangeValue={onChange}
      />
    </Stack>
  </FormControl>
);

/**
 * Sign-in view component rendering phone number authentication form
 *
 * Displays country code picker and phone number input with real-time validation
 * and formatting based on selected country code.
 * @param root0 - Component props
 * @param root0.control - React Hook Form control object for form state management
 * @param root0.valid - Whether form is valid and ready for submission
 * @param root0.widthContent - Content width for responsive layout
 * @param root0.onSubmit - Callback invoked when sign-in form is submitted
 * @param root0.loading - Whether sign-in request is in progress
 * @param root0.isDark - Whether dark theme is active
 * @param root0.renderMobileNumber - The memoized mobile-number field renderer
 */
const SignInView = ({
  control,
  valid,
  widthContent,
  onSubmit,
  loading,
  isDark,
  renderMobileNumber,
}: Props) => (
  <Screen
    title="Log In"
    widthContent={widthContent}
    subTitle="Let's verify your account with your mobile number."
    footer={
      <Button
        testID="SIGNIN.BUTTON_NEXT"
        isDisabled={loading || !valid}
        onPress={onSubmit}
        className={isDark ? "bg-surface-subtle" : "bg-surface-muted"}
        size="xl"
      >
        <ButtonText
          className={`font-body ${ifWeb("text-title", "")} ${!loading && valid ? "text-status-info" : "text-content-muted"}`}
        >
          {!loading ? "Next" : "Please Wait..."}
        </ButtonText>
      </Button>
    }
  >
    <Stack direction="horizontal" className="items-start gap-3 px-3">
      <Controller
        name="countryCode"
        control={control}
        render={renderCountryCodeField}
      />

      <Controller
        name="mobileNumber"
        control={control}
        render={renderMobileNumber}
      />
    </Stack>
  </Screen>
);

SignInView.displayName = "SignInView";

export default memo(SignInView);
